import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, GeoJSON, ImageOverlay, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Legend from './MapLegend';
import DashboardYearStrip from './Dashboard-Year-Strip';
import bgImage from '../assets/images/layers.png';

// Tile layer options
const tileLayers = {
  osm: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap contributors',
  },
  terrain: {
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenTopoMap contributors',
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; Esri & NASA Earthstar Geographics',
  },
};

// Component to handle bounding box zoom
const BboxZoom = ({ bounds, boundsKey }) => {
  const map = useMap();
  const hasZoomedRef = useRef(null); // Track boundsKey to allow re-zoom on bounds change

  useEffect(() => {
    if (bounds && bounds.isValid() && hasZoomedRef.current !== boundsKey && map) {
      console.log("BboxZoom triggered with bounds:", bounds, "boundsKey:", boundsKey);
      // Fit the map to the bounds
      map.fitBounds(bounds, { padding: [20, 20], animate: false });
      // Calculate the center of the bounds
      const boundsCenter = bounds.getCenter();
      console.log("Bounds center:", boundsCenter);
      // Increment zoom by 1, centered on bounds' center
      setTimeout(() => {
        const newZoom = Math.min(map.getZoom() + 1, map.getMaxZoom()); // Respect max zoom
        console.log("Zooming to:", newZoom, "Centered at:", boundsCenter);
        map.setView(boundsCenter, newZoom, { animate: false });
        map.panBy([170, 0], { animate: false });
        hasZoomedRef.current = boundsKey; // Update to current boundsKey
      }, 300);
    }
  }, [bounds, boundsKey, map]);

  return null;
};

const TestMap = ({
  selectedHazardMap,
  secondaryGeoJson,
  showHazard,
  showRiverBasin,
  showRivers,
  years,
  isTemporal,
  onSliderChange,
  timeLineToggle,
  setShowAggregated,
  showAggregated,
  selectedCountries,
  geoJsonData,
  selectedYear,
  selectedBasins,
  filterBy
}) => {
  console.log('TestMap Props:', {
    selectedHazardMap,
    secondaryGeoJson,
    showHazard,
    showRiverBasin,
    showRivers,
    years,
    isTemporal,
    timeLineToggle,
    showAggregated,
    selectedCountries,
    geoJsonData,
    selectedYear,
    selectedBasins,
    filterBy
  });
  const [mapGeoJsonData, setMapGeoJsonData] = useState(null);
  const [secondaryData, setSecondaryData] = useState(secondaryGeoJson || null);
  const [opacityMap, setOpacityMap] = useState({});
  const [bbox, setBbox] = useState(null);
  const [showTileOptions, setShowTileOptions] = useState(false);
  const [countryGeoJson, setCountryGeoJson] = useState(null);
  const geoJsonLayerRef = useRef(null);
  const secondaryGeoJsonLayerRef = useRef(null);
  const countryGeoJsonLayerRef = useRef(null);
  const currentTooltip = useRef(null);
  const mapRef = useRef(null);
  const [tileLayer, setTileLayer] = useState('osm');
  const isInitialRender = useRef(true); // Track initial render to prevent premature fallback

  const legendData = useMemo(() => selectedHazardMap?.legend || null, [selectedHazardMap]);
  const defaultZoom = useMemo(() => ({ coordinates: [0.0, 20.0], zoom: 4 }), []);

  // Filter basins based on selectedBasins
  const filteredBasinsGeoJson = useMemo(() => {
    if (!secondaryGeoJson?.geojson_basins || !showRiverBasin) {
      console.log('No basins to filter: secondaryGeoJson.geojson_basins or showRiverBasin is falsy');
      return null;
    }
    if (!selectedBasins || selectedBasins.length === 0) {
      console.log('No selected basins, returning all basins:', secondaryGeoJson.geojson_basins);
      return secondaryGeoJson.geojson_basins;
    }
    const selectedBasinCodes = selectedBasins.map(basin => String(basin.value));
    console.log('Selected basin codes:', selectedBasinCodes);
    console.log('Available basin features:', secondaryGeoJson.geojson_basins.features);
    const filteredFeatures = secondaryGeoJson.geojson_basins.features.filter(feature => {
      const basinCode = String(feature.properties?.MAJ_BAS);
      const isIncluded = selectedBasinCodes.includes(basinCode);
      console.log(`Checking MAJ_BAS: ${basinCode}, Included: ${isIncluded}`);
      return isIncluded;
    });
    const filteredGeoJson = {
      type: 'FeatureCollection',
      features: filteredFeatures,
    };
    console.log('Filtered basins GeoJSON:', filteredGeoJson);
    return filteredGeoJson;
  }, [secondaryGeoJson, selectedBasins, showRiverBasin]);

  // Create a unique key for bounds to track changes
  const boundsKey = useMemo(() => {
    return JSON.stringify({
      geoJsonData: geoJsonData?.features?.length,
      selectedCountries: selectedCountries?.map(c => c.value).sort(),
      filteredBasins: filteredBasinsGeoJson?.features?.length,
      showRiverBasin,
      showHazard
    });
  }, [geoJsonData, selectedCountries, filteredBasinsGeoJson, showRiverBasin, showHazard]);

  // Tile layer button options
  const tileLayerOptions = [
    { key: 'osm', label: 'Street Map', icon: '../assets/images/StreetMap.png' },
    { key: 'terrain', label: 'Terrain', icon: '../assets/images/TerrainMap.png' },
    { key: 'satellite', label: 'Satellite', icon: '../assets/images/SatellitesMap.png' },
  ];

  // Fetch country GeoJSON
  useEffect(() => {
    const fetchCountryGeojson = async () => {
      try {
        const response = await fetch('/data/p2/adm0_p2.geojson');
        if (!response.ok) throw new Error(`Failed to fetch adm0_p2.geojson: ${response.status}`);
        const data = await response.json();
        setCountryGeoJson(data);
      } catch (error) {
        console.error('Failed to fetch country GeoJSON:', error);
        setCountryGeoJson(null);
      }
    };

    fetchCountryGeojson();
  }, []);

  // Toggle tile options visibility
  const toggleTileOptions = () => {
    setShowTileOptions((prev) => !prev);
  };

  // Compute filtered GeoJSON based on selectedCountries
  const getFilteredGeoJSON = useCallback(() => {
    if (!geoJsonData || !selectedCountries || selectedCountries.length === 0) {
      return geoJsonData;
    }
    const filtered = geoJsonData.features.filter((f) =>
      selectedCountries.some((country) => country.value === f.properties.ADM0_CODE)
    );
    return {
      type: 'FeatureCollection',
      features: filtered,
    };
  }, [geoJsonData, selectedCountries]);

  // Calculate bounding box for filtered GeoJSON and filteredBasinsGeoJson
  const getBbox = useCallback(() => {
    let combinedBounds = null;

    // Calculate bounds for filtered GeoJSON
    const geojson = getFilteredGeoJSON();
    if (showHazard && geojson && geojson.features?.length > 0) {
      try {
        const layer = L.geoJSON(geojson);
        const bounds = layer.getBounds();
        if (bounds.isValid()) {
          combinedBounds = bounds;
          console.log('GeoJSON bounds:', bounds);
        } else {
          console.warn('Invalid bounds calculated for filtered GeoJSON');
        }
      } catch (error) {
        console.error('Error calculating GeoJSON bounds:', error);
      }
    } else {
      console.log('No features in filtered GeoJSON or showHazard is false');
    }

    // Calculate bounds for filteredBasinsGeoJson if showRiverBasin is true
    if (showRiverBasin && filteredBasinsGeoJson && filteredBasinsGeoJson.features?.length > 0) {
      try {
        const basinLayer = L.geoJSON(filteredBasinsGeoJson);
        const basinBounds = basinLayer.getBounds();
        if (basinBounds.isValid()) {
          if (combinedBounds) {
            combinedBounds = combinedBounds.extend(basinBounds);
            console.log('Extended bounds with basins:', combinedBounds);
          } else {
            combinedBounds = basinBounds;
            console.log('Basin bounds:', basinBounds);
          }
        } else {
          console.warn('Invalid bounds calculated for filteredBasinsGeoJson');
        }
      } catch (error) {
        console.error('Error calculating filteredBasinsGeoJson bounds:', error);
      }
    } else {
      console.log('No features in filteredBasinsGeoJson or showRiverBasin is false');
    }

    if (combinedBounds && combinedBounds.isValid()) {
      setBbox(combinedBounds);
      console.log('Combined bounds set:', combinedBounds);
    } else {
      setBbox(null);
      console.log('No valid bounds calculated, will use default zoom');
    }
  }, [getFilteredGeoJSON, filteredBasinsGeoJson, showRiverBasin, showHazard]);

  // Trigger bbox calculation when dependencies change
  useEffect(() => {
    console.log('Triggering getBbox with:', { showHazard, hasGeoJsonData: !!geoJsonData, showRiverBasin, hasFilteredBasinsGeoJson: !!filteredBasinsGeoJson });
    if ((showHazard && geoJsonData && geoJsonData.features?.length > 0) || (showRiverBasin && filteredBasinsGeoJson && filteredBasinsGeoJson.features?.length > 0)) {
      getBbox();
    } else {
      setBbox(null);
      console.log('No valid data for bbox calculation, setting bbox to null');
    }
    isInitialRender.current = false; // Mark initial render as complete
  }, [geoJsonData, selectedCountries, filteredBasinsGeoJson, showRiverBasin, showHazard, getBbox]);

  // Update map view for fallback zoom
  const MapViewUpdater = () => {
    const map = useMap();
    useEffect(() => {
      // Only apply default zoom if not initial render and no valid bbox
      if (!isInitialRender.current && (!bbox || !bbox.isValid())) {
        console.log('No valid bbox, applying default zoom:', defaultZoom);
        map.setView(defaultZoom.coordinates, defaultZoom.zoom, { animate: false });
        map.panBy([170, 0], { animate: false });
        map.invalidateSize();
      }
    }, [map, bbox]);
    return null;
  };

  const getHazardLabel = useCallback((hazard) => {
    const symbols = hazard?.split('') || [];
    const legend = [
      { hazard_symbol: 'F', hazard: 'Flood' },
      { hazard_symbol: 'D', hazard: 'Drought' },
      { hazard_symbol: 'Q', hazard: 'Earthquake' },
      { hazard_symbol: 'E', hazard: 'Epidemic' },
      { hazard_symbol: 'T', hazard: 'Extreme Temperature' },
      { hazard_symbol: 'L', hazard: 'Landslide' },
      { hazard_symbol: 'S', hazard: 'Storm' },
      { hazard_symbol: 'V', hazard: 'Volcanic Activity' },
      { hazard_symbol: 'W', hazard: 'Wildfire' },
    ];
    return (
      symbols
        .map((symbol, i) => {
          const hazardObj = legend.find((item) => item.hazard_symbol === symbol);
          return hazardObj ? (i > 0 ? ' + ' : '') + hazardObj.hazard : '';
        })
        .join('') || 'Unknown Hazard'
    );
  }, []);

  const closeOtherTooltips = useCallback((layer) => {
    if (currentTooltip.current && currentTooltip.current !== layer) {
      currentTooltip.current.closeTooltip();
    }
    currentTooltip.current = layer;
  }, []);

  const initializeOpacityMap = useCallback((legendData) => {
    if (legendData) {
      const initialOpacity = legendData.reduce((acc, item) => {
        acc[item.color] = 0.8;
        return acc;
      }, {});
      setOpacityMap(initialOpacity);
    }
  }, []);

  const handleOpacityChange = useCallback((color, newOpacity) => {
    setOpacityMap((prev) => ({
      ...prev,
      [color]: newOpacity,
    }));
  }, []);

  const hoverStyle = useCallback(
    (color) => ({
      color: 'red',
      weight: 1,
      fillOpacity: opacityMap[color] !== undefined ? opacityMap[color] : 0.8,
    }),
    [opacityMap]
  );

  const onEachFeature = useCallback(
    (feature, layer, type) => {
      let color = 'transparent';
      if (type === 'primary') {
        if (!feature?.properties || !feature.properties.country) {
          layer.unbindTooltip();
          layer.options.interactive = false;
          color = feature?.properties?.color || 'transparent';
        } else {
          const districtName =
            feature.properties.ADM2_NAME === 'Administrative unit not available'
              ? 'N/A'
              : feature.properties.ADM2_NAME || 'Unnamed District';
          const countryName = feature.properties.country;
          let tooltipText = `District: ${districtName}<br/>Country: ${countryName}`;
          if (feature.properties.symbol) {
            tooltipText += `<br/>Hazards: ${getHazardLabel(feature.properties.symbol)}`;
          }
          if (isTemporal && feature.properties.temporal) {
            const intensity = feature.properties.temporal[selectedYear] || 0;
            tooltipText += `<br/>Year: ${selectedYear}<br/>Intensity: ${intensity}`;
          }
          const countryIds = selectedCountries?.map((country) => country.value) || [];
          if (!selectedCountries || selectedCountries.length === 0 || countryIds.includes(feature.properties.ADM0_CODE)) {
            layer.bindTooltip(tooltipText, { sticky: true });
            layer.options.interactive = true;
          } else {
            layer.unbindTooltip();
            layer.options.interactive = false;
          }
          color = feature.properties.color || 'transparent';
        }
      } else if (type === 'country') {
        const isSelected = selectedCountries?.some((country) => country.value === feature.properties.ADM0_CODE);
        color = isSelected ? '#000000' : 'transparent';
        layer.setStyle({
          weight: isSelected ? 1 : 0,
          color: color,
          fillOpacity: 0,
        });
        layer.options.interactive = true;
        layer.options.zIndex = 1;
      } else if (type === 'basin') {
        layer.setStyle({
          weight: 1,
          color: 'red',
          fillOpacity: 0,
        });
        layer.options.interactive = false;
        layer.unbindTooltip();
        layer.options.zIndex = 2;
      }

      if (type !== 'basin') {
        layer.on({
          tooltipopen: () => closeOtherTooltips(layer),
          click: () => {
            layer.setStyle({
              weight: type === 'country' ? (selectedCountries?.some((country) => country.value === feature.properties.ADM0_CODE) ? 1 : 0) : 0.3,
              color: type === 'primary' ? color : type === 'country' ? color : 'red',
              fillColor: type === 'country' ? 'transparent' : color,
              fillOpacity: type === 'country' ? 0 : (opacityMap[color] !== undefined ? opacityMap[color] : 0.8),
            });
          },
        });

        if (feature?.geometry?.type === 'Polygon' || feature?.geometry?.type === 'MultiPolygon') {
          layer.setStyle({
            weight: type === 'country' ? (selectedCountries?.some((country) => country.value === feature.properties.ADM0_CODE) ? 1 : 0) : 0.3,
            color: type === 'primary' ? color : type === 'country' ? color : 'red',
            fillColor: type === 'country' ? 'transparent' : color,
            fillOpacity: type === 'country' ? 0 : (opacityMap[color] !== undefined ? opacityMap[color] : 0.8),
            zIndex: type === 'primary' ? 3 : type === 'country' ? 1 : 2,
          });

          layer.on({
            mouseover: () => {
              if (type !== 'country') {
                layer.setStyle(hoverStyle(color));
              }
            },
            mouseout: () => {
              layer.setStyle({
                weight: type === 'country' ? (selectedCountries?.some((country) => country.value === feature.properties.ADM0_CODE) ? 1 : 0) : 0.3,
                color: type === 'primary' ? color : type === 'country' ? color : 'red',
                fillColor: type === 'country' ? 'transparent' : color,
                fillOpacity: type === 'country' ? 0 : (opacityMap[color] !== undefined ? opacityMap[color] : 0.8),
                zIndex: type === 'primary' ? 3 : type === 'country' ? 1 : 2,
              });
            },
          });
        } else if (feature?.geometry?.type === 'Point' && type === 'primary' && feature?.properties?.country) {
          const countryIds = selectedCountries?.map((country) => country.value) || [];
          if (!selectedCountries || selectedCountries.length === 0 || countryIds.includes(feature.properties.ADM0_CODE)) {
            layer.bindTooltip(`Point feature: ${feature.properties?.name || 'Unnamed Point'}`, { sticky: true });
            layer.options.interactive = true;
          } else {
            layer.unbindTooltip();
            layer.options.interactive = false;
          }
        }
      }
    },
    [getHazardLabel, closeOtherTooltips, hoverStyle, selectedCountries, opacityMap, isTemporal, selectedYear]
  );

  // Update geoJsonData and initialize opacity
  useEffect(() => {
    if (selectedHazardMap?.geojson && selectedHazardMap.geojson.type === 'FeatureCollection') {
      setMapGeoJsonData(selectedHazardMap.geojson);
      initializeOpacityMap(selectedHazardMap.legend);
    } else {
      setMapGeoJsonData(null);
      console.warn('TestMap: Invalid or missing GeoJSON data in selectedHazardMap');
    }
  }, [selectedHazardMap, initializeOpacityMap]);

  // Update secondary data
  useEffect(() => {
    if (secondaryGeoJson) {
      setSecondaryData({
        ...secondaryGeoJson,
        geojson_basins: filteredBasinsGeoJson,
      });
    } else {
      setSecondaryData(null);
    }
  }, [secondaryGeoJson, filteredBasinsGeoJson]);

  // Update layer opacity
  useEffect(() => {
    if (geoJsonLayerRef.current) {
      geoJsonLayerRef.current.eachLayer((layer) => {
        const color = layer.feature?.properties?.color || 'transparent';
        if (opacityMap[color] !== undefined) {
          layer.setStyle({
            fillOpacity: opacityMap[color],
            zIndex: 3,
          });
        }
      });
    }
  }, [opacityMap]);

  // Update country layer when selectedCountries or countryGeoJson changes
  useEffect(() => {
    if (countryGeoJsonLayerRef.current && countryGeoJson) {
      countryGeoJsonLayerRef.current.eachLayer((layer) => {
        const feature = layer.feature;
        const isSelected = selectedCountries?.some((country) => country.value === feature.properties.ADM0_CODE);
        layer.setStyle({
          weight: isSelected ? 1 : 0,
          color: isSelected ? '#000000' : 'transparent',
          fillOpacity: 0,
          zIndex: 1,
        });
      });
    }
  }, [selectedCountries, countryGeoJson]);

  // Update tooltips and interactivity
  useEffect(() => {
    if (!showRiverBasin && geoJsonLayerRef.current && showHazard && mapGeoJsonData) {
      geoJsonLayerRef.current.eachLayer((layer) => {
        const feature = layer.feature;
        if (feature?.properties && feature.properties.country) {
          const districtName =
            feature.properties.ADM2_NAME === 'Administrative unit not available'
              ? 'N/A'
              : feature.properties.ADM2_NAME || 'Unnamed District';
          const countryName = feature.properties.country;
          let tooltipText = `District: ${districtName}<br/>Country: ${countryName}`;
          if (feature.properties.symbol) {
            tooltipText += `<br/>Hazards: ${getHazardLabel(feature.properties.symbol)}`;
          }
          if (isTemporal && feature.properties.temporal) {
            const intensity = feature.properties.temporal[selectedYear] || 0;
            tooltipText += `<br/>Year: ${selectedYear}<br/>Intensity: ${intensity}`;
          }
          const countryIds = selectedCountries?.map((country) => country.value) || [];
          if (!selectedCountries || selectedCountries.length === 0 || countryIds.includes(feature.properties.ADM0_CODE)) {
            layer.bindTooltip(tooltipText, { sticky: true });
            layer.options.interactive = true;
            layer.options.zIndex = 3;
          } else {
            layer.unbindTooltip();
            layer.options.interactive = false;
          }
        } else {
          layer.unbindTooltip();
          layer.options.interactive = false;
        }
      });
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }
  }, [showRiverBasin, showHazard, mapGeoJsonData, selectedCountries, getHazardLabel, isTemporal, selectedYear]);

  // Remove outline from interactive paths
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const paths = document.querySelectorAll('path.leaflet-interactive');
      paths.forEach((path) => {
        path.style.outline = 'none';
      });
    });

    const mapContainer = document.querySelector('.leaflet-container');
    if (mapContainer) {
      observer.observe(mapContainer, { childList: true, subtree: true });
    }

    return () => observer.disconnect();
  }, []);

  const base64ToDataUrl = useCallback((base64) => `data:image/png;base64,${base64}`, []);

  // Cleanup layers on unmount
  useEffect(() => {
    return () => {
      if (geoJsonLayerRef.current) {
        geoJsonLayerRef.current.clearLayers();
      }
      if (secondaryGeoJsonLayerRef.current) {
        secondaryGeoJsonLayerRef.current.clearLayers();
      }
      if (countryGeoJsonLayerRef.current) {
        countryGeoJsonLayerRef.current.clearLayers();
      }
    };
  }, []);

  return (
    <>
      <div style={{ height: '81vh' }}>
        {showTileOptions && (
          <div className="card legendCard five1 border-0 bg-transparent" style={isTemporal ? { bottom: '75px' } : {}}>
            <div className="card-header bg-white px-1 pt-1 pb-1 border-0 d-flex pb-1 align-items-center btnGroup">
              {tileLayerOptions.map(({ key, label, icon }) => (
                <button
                  key={key}
                  onClick={() => setTileLayer(key)}
                  className={`btn border-0 ${tileLayer === key ? 'active' : ''}`}
                  style={{ marginRight: '5px' }}
                  aria-label={`Switch to ${label} map`}
                  aria-pressed={tileLayer === key}
                >
                  <span>
                    <img src={icon} loading="lazy" alt={`${label} map icon`} height="20px" />
                  </span>{' '}
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
        <MapContainer
          key={`map-${tileLayer}-${boundsKey}-${isTemporal}`}
          center={defaultZoom.coordinates}
          zoom={defaultZoom.zoom}
          style={{ height: '100vh', width: '100%', marginTop: '-16px', position: 'fixed' }}
          preferCanvas={true}
          ref={mapRef}
        >
          {bbox && <BboxZoom bounds={bbox} boundsKey={boundsKey} />}
          <MapViewUpdater />
          <TileLayer
            url={tileLayers[tileLayer].url}
            attribution={tileLayers[tileLayer].attribution}
          />

          {/* Render country layer first to ensure it is below the district layer */}
          {countryGeoJson && countryGeoJson.features?.length > 0 && (
            <GeoJSON
              key={`country-${selectedCountries?.length || 0}-${Date.now()}`}
              data={countryGeoJson}
              onEachFeature={(feature, layer) => onEachFeature(feature, layer, 'country')}
              ref={countryGeoJsonLayerRef}
              interactive={true}
              style={{ zIndex: 1 }}
            />
          )}

          {showHazard && mapGeoJsonData && mapGeoJsonData.features?.length > 0 && (
            <GeoJSON
              key={`primary-${isTemporal ? selectedYear : mapGeoJsonData.features.length}-${Date.now()}`}
              data={mapGeoJsonData}
              onEachFeature={(feature, layer) => onEachFeature(feature, layer, 'primary')}
              ref={geoJsonLayerRef}
              interactive={true}
              style={{ zIndex: 3 }}
            />
          )}

          {showRivers && secondaryData?.base64_rivers && secondaryData?.bbox && (
            <ImageOverlay
              key={`rivers-${secondaryData.base64_rivers}`}
              url={base64ToDataUrl(secondaryData.base64_rivers)}
              bounds={[
                [secondaryData.bbox[1], secondaryData.bbox[0]],
                [secondaryData.bbox[3], secondaryData.bbox[2]],
              ]}
              interactive={false}
              zIndex={2}
            />
          )}

          {showRiverBasin && filteredBasinsGeoJson && filteredBasinsGeoJson.features?.length > 0 && (
            <GeoJSON
              key={`secondary-${filteredBasinsGeoJson.features.length}-${Date.now()}`}
              data={filteredBasinsGeoJson}
              onEachFeature={(feature, layer) => onEachFeature(feature, layer, 'basin')}
              ref={secondaryGeoJsonLayerRef}
              interactive={false}
              style={{ zIndex: 2 }}
            />
          )}
        </MapContainer>

        {legendData && (
          <Legend
            legendData={legendData}
            onOpacityChange={handleOpacityChange}
            isTemporal={isTemporal}
            setShowAggregated={setShowAggregated}
            showAggregated={showAggregated}
          />
        )}

        {selectedHazardMap && isTemporal && (
          <DashboardYearStrip
            onSliderChange={(year) => {
              onSliderChange(year);
            }}
            years={years}
            timeLineToggle={timeLineToggle}
            setShowAggregated={setShowAggregated}
            showAggregated={showAggregated}
          />
        )}
      </div>

      <div
        className="card legendCard ten border-0"
        style={{
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          fontSize: '12px !important',
          fontWeight: '600 !important',
          position: 'fixed',
          bottom: isTemporal ? '80px' : '12px',
          left: '306px',
          cursor: 'pointer',
          backgroundColor: 'transparent',
        }}
        onClick={toggleTileOptions}
      >
        <p className="mb-0 text-white">
          <span>
            <img src="../assets/images/layers1.png" />
          </span>{' '}
          Layers
        </p>
      </div>
    </>
  );
};

export default TestMap;